import React, { useEffect, useState } from 'react';
import { IPhoto } from '../../model/shared/IPhoto';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Button, Carousel } from 'react-bootstrap';
import { IInvoice } from '../../model/invoice/Invoices';
import { getFileType } from '../../utils/Functions';
import { IBase } from '../../model/shared/IBase';

export interface IFancyboxFile extends IBase {
    name: string;
    path?: string;
    reportPath?: string;
}

interface CustomFancyboxProps {
    files: IPhoto[] | IInvoice[] | IFancyboxFile[];
    keyPath?: 'path' | 'reportPath';
    isView?: boolean;
    hideTrigger?: boolean;
    open?: boolean;
    onClose?: () => void;
    variant?: string;
}

const CustomFancybox = ({
    files,
    keyPath = 'path',
    isView = false,
    hideTrigger = false,
    open,
    onClose,
    variant = 'primary',
}: CustomFancyboxProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);

    useEffect(() => {
        if (open !== undefined) {
            setIsOpen(open);
        }
    }, [open]);

    const openModal = (index: number) => {
        setPhotoIndex(index);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setPhotoIndex(0);
        onClose?.();
    };

    const nextImage = () => {
        if (photoIndex + 1 <= files.length - 1) {
            setPhotoIndex(prevIndex => (prevIndex + 1) % files.length);
        }
    };

    const prevImage = () => {
        if (photoIndex - 1 >= 0) {
            setPhotoIndex(prevIndex => (prevIndex - 1 + files.length) % files.length);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeModal();
            } else if (event.key === 'ArrowLeft') {
                prevImage();
            } else if (event.key === 'ArrowRight') {
                nextImage();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, photoIndex]);

    return (
        <div className="fancybox" style={{ width: isView ? 'auto' : 460 }}>
            {!!isView && !hideTrigger && (
                <Button variant={variant} onClick={() => setIsOpen(true)}>
                    View
                </Button>
            )}
            {!isView && (
                <div>
                    <Carousel
                        className="custom-carousel-indicators px-19"
                        controls={files?.length > 1}
                        indicators={files?.length > 1}
                        activeIndex={photoIndex}
                        onSelect={e => setPhotoIndex(e)}
                        interval={null}
                        nextIcon={
                            <div className="icon-circle">
                                <FaArrowRight style={{ fontSize: '2rem' }} />
                            </div>
                        }
                        prevIcon={
                            <div className="icon-circle">
                                <FaArrowLeft style={{ fontSize: '2rem' }} />
                            </div>
                        }
                    >
                        {files?.length ? (
                            files.map((item, imgIndex) => (
                                <Carousel.Item
                                    key={item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] + '' + item.id}
                                    className="text-center cursor-pointer"
                                >
                                    {getFileType(files[photoIndex].name).type === 'pdf' ? (
                                        <div onClick={() => openModal(imgIndex)}>
                                            <iframe
                                                src={
                                                    item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string
                                                }
                                                style={{
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    maxHeight: 700,
                                                    pointerEvents: 'none',
                                                }}
                                                className="rounded"
                                                title={item?.name}
                                            />
                                        </div>
                                    ) : (
                                        <img
                                            alt="screenshot"
                                            src={item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string}
                                            onClick={() => openModal(imgIndex)}
                                            style={{ maxWidth: '100%', height: 'auto', maxHeight: 700 }}
                                            className="rounded"
                                        />
                                    )}
                                </Carousel.Item>
                            ))
                        ) : (
                            <img
                                alt=""
                                src="/files/placeholder.png"
                                style={{ maxWidth: '100%', height: 'auto' }}
                                className="rounded"
                            />
                        )}
                    </Carousel>
                </div>
            )}

            {files?.[photoIndex] && (
                <div className={`fancybox-modal ${isOpen ? 'show' : ''}`} onClick={closeModal}>
                    <div className="holder-fullscreen">
                        {getFileType(files[photoIndex].name).type === 'pdf' ? (
                            <iframe
                                src={files[photoIndex][keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string}
                                className="rounded w-100 h-100"
                                onClick={e => e.stopPropagation()}
                                title={files[photoIndex].name}
                            />
                        ) : (
                            <img
                                src={files[photoIndex][keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string}
                                alt="Fullscreen"
                                className="rounded w-100 h-100"
                                onClick={e => e.stopPropagation()}
                            />
                        )}
                    </div>

                    <div className="d-flex mt-5 mb-2 gap-2 align-items-center">
                        {files?.length ? (
                            files.map((item, imgIndex) => (
                                <div
                                    key={
                                        (item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string) +
                                        '' +
                                        item.id
                                    }
                                    className="text-center cursor-pointer"
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                >
                                    {getFileType(item.name).type === 'pdf' ? (
                                        <iframe
                                            src={item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string}
                                            onClick={() => openModal(imgIndex)}
                                            style={{
                                                height: photoIndex === imgIndex ? 110 : 100,
                                                width: photoIndex === imgIndex ? 110 : 100,
                                                maxHeight: 700,
                                            }}
                                            className={`rounded img-cover fancybox-gallery-img ${
                                                photoIndex === imgIndex ? 'indexed' : ''
                                            }`}
                                            title={item.name}
                                        />
                                    ) : (
                                        <img
                                            alt="screenshot"
                                            src={item[keyPath as keyof (IPhoto | IInvoice | IFancyboxFile)] as string}
                                            onClick={() => openModal(imgIndex)}
                                            style={{
                                                height: photoIndex === imgIndex ? 110 : 100,
                                                width: photoIndex === imgIndex ? 110 : 100,
                                                maxHeight: 700,
                                            }}
                                            className={`rounded img-cover fancybox-gallery-img ${
                                                photoIndex === imgIndex ? 'indexed' : ''
                                            }`}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            <img
                                alt="placeholder"
                                src="/files/placeholder.png"
                                style={{ maxWidth: '100%', height: 'auto' }}
                                className="rounded"
                            />
                        )}
                    </div>

                    {photoIndex > 0 && (
                        <button
                            className="btn position-absolute cursor-pointer text-white"
                            onClick={e => {
                                e.stopPropagation();
                                prevImage();
                            }}
                            style={{
                                left: '5%',
                                fontSize: '2rem',
                            }}
                        >
                            <div className="icon-circle">
                                <FaArrowLeft style={{ fontSize: '2rem' }} />
                            </div>
                        </button>
                    )}
                    {photoIndex < files.length - 1 && (
                        <button
                            className="btn position-absolute cursor-pointer text-white"
                            onClick={e => {
                                e.stopPropagation();
                                nextImage();
                            }}
                            style={{
                                right: '5%',
                                fontSize: '2rem',
                            }}
                        >
                            <div className="icon-circle">
                                <FaArrowRight style={{ fontSize: '2rem' }} />
                            </div>
                        </button>
                    )}

                    <button
                        className="btn position-absolute cursor-pointer text-white"
                        onClick={closeModal}
                        style={{
                            top: 20,
                            right: 20,
                            fontSize: '2rem',
                        }}
                    >
                        ✖
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomFancybox;
